/**
 * Match Start Node - Player View
 *
 * Dramatic launch-sequence countdown. The most hype moment in the flow —
 * massive countdown number with glow, tactical briefing summary, cinematic
 * "good luck" sendoff. No player actions needed, pure atmosphere.
 */

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import type { NodePlayerViewProps } from "../types";

type MatchSummary = {
  map?: string;
  gameMode?: string;
  server?: string;
};

type MatchStartState = {
  countdownStarted?: boolean;
  countdownComplete?: boolean;
  matchSummary?: MatchSummary;
};

export function MatchStartView({ nodeState }: NodePlayerViewProps<MatchStartState>) {
  const state = nodeState.state as MatchStartState;
  const config = nodeState.config as {
    countdownSeconds?: number;
    displayMessage?: string;
    showMap?: boolean;
    labels?: { title?: string };
  };
  const title = config.labels?.title ?? config.displayMessage ?? "Match Starting";
  const configSeconds = config.countdownSeconds ?? 5;

  const initialCountdown = useMemo(() => {
    if (nodeState.timeoutAt) {
      return Math.max(0, Math.ceil((nodeState.timeoutAt - Date.now()) / 1000));
    }
    return configSeconds;
  }, [nodeState.timeoutAt, configSeconds]);

  const [countdown, setCountdown] = useState(initialCountdown);

  useEffect(() => {
    setCountdown(initialCountdown);
  }, [initialCountdown]);

  useEffect(() => {
    if (!state.countdownStarted || state.countdownComplete) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [state.countdownStarted, state.countdownComplete]);

  const summaryItems: { label: string; value: string }[] = [];
  if (config.showMap !== false && state.matchSummary?.map) {
    summaryItems.push({ label: "Map", value: state.matchSummary.map });
  }
  if (state.matchSummary?.gameMode) {
    summaryItems.push({ label: "Mode", value: state.matchSummary.gameMode });
  }
  if (state.matchSummary?.server) {
    summaryItems.push({ label: "Server", value: state.matchSummary.server });
  }

  return (
    <div className="@container relative overflow-hidden rounded-xl bg-surface-900 border border-surface-700/50">
      {/* Atmospheric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_30%,rgba(38,217,181,0.04),transparent_70%)]" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/15 to-transparent" />
      </div>

      <div className="relative z-10 px-4 @md:px-8 py-6 @md:py-8">
        {/* Title */}
        <motion.h3
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center font-display text-sm @md:text-base font-bold uppercase tracking-[0.3em] text-brand-400 mb-6"
        >
          {title}
        </motion.h3>

        {/* Countdown / Complete / Waiting */}
        <AnimatePresence mode="wait">
          {state.countdownStarted && !state.countdownComplete && (
            <motion.div
              key="countdown"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="text-center mb-6"
            >
              <div className="relative inline-flex items-center justify-center w-40 h-40 @sm:w-48 @sm:h-48">
                {/* Outer pulsing ring */}
                <motion.div
                  className="absolute inset-0 rounded-full border border-brand-500/20"
                  animate={{ scale: [1, 1.12, 1], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                />
                {/* Inner ring */}
                <div className="absolute inset-3 rounded-full border-2 border-brand-500/30" />
                {/* The number */}
                <motion.span
                  key={countdown}
                  initial={{ scale: 1.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 250, damping: 18 }}
                  className="relative font-display text-8xl @sm:text-9xl font-bold text-brand-400 tabular-nums"
                  style={{
                    textShadow: "0 0 40px rgba(38,217,181,0.4), 0 0 80px rgba(38,217,181,0.15)",
                  }}
                >
                  {countdown}
                </motion.span>
              </div>
            </motion.div>
          )}

          {state.countdownComplete && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="text-center mb-6"
            >
              <div className="relative inline-flex items-center justify-center mb-3">
                <motion.div
                  className="absolute w-24 h-24 rounded-full bg-brand-500/10"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                />
                <div className="w-20 h-20 rounded-full bg-brand-500/15 border border-brand-500/30 flex items-center justify-center">
                  <motion.svg
                    aria-hidden="true"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-brand-400"
                  >
                    <motion.path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    />
                  </motion.svg>
                </div>
              </div>
              <p
                className="font-display text-2xl @sm:text-3xl font-bold uppercase tracking-wider text-brand-400"
                style={{ textShadow: "0 0 30px rgba(38,217,181,0.3)" }}
              >
                Match Started
              </p>
            </motion.div>
          )}

          {!state.countdownStarted && !state.countdownComplete && (
            <motion.div
              key="waiting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center mb-6"
            >
              <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-surface-800/60 border border-surface-700/40">
                <div className="w-4 h-4 rounded-full border-2 border-surface-500/50 border-t-brand-400/80 animate-spin" />
                <span className="text-sm text-text-secondary">Preparing match...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tactical briefing — inline summary */}
        {summaryItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="flex items-center justify-center mb-6"
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/60 border border-surface-600/30">
              {summaryItems.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.3 + i * 0.08 }}
                  className="flex items-center gap-2"
                >
                  {i > 0 && <div className="w-px h-4 bg-surface-500/30" />}
                  <span className="text-2xs font-display font-semibold uppercase tracking-widest text-surface-500">
                    {item.label}
                  </span>
                  <span className="font-display font-bold text-sm text-white">{item.value}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* GLHF sendoff */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="text-center"
        >
          <p
            className="font-display text-lg @sm:text-xl font-bold uppercase tracking-widest text-brand-400/90"
            style={{ textShadow: "0 0 24px rgba(38,217,181,0.25)" }}
          >
            Good luck, have fun
          </p>
        </motion.div>
      </div>
    </div>
  );
}
